import React, { useCallback, useEffect, useState } from "react";
import adminService from "../../api/services/adminService";
import PaginationBar from "../../components/common/PaginationBar.jsx";
import { TableSkeleton } from "../../components/common/skeletons";
import toast from "react-hot-toast";
import { confirmDanger } from "../../utils/confirmDialog";

export default function ReferralProgram() {
  const [redemptions, setRedemptions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchRedemptions = useCallback(async (p = 1) => {
    setLoading(true);
    setError("");
    try {
      const res = await adminService.getReferralRedemptions({ page: p, limit: 20 });
      if (res?.success) {
        setRedemptions(res.redemptions || []);
        setTotalPages(res.pagination?.totalPages || 1);
        setTotalItems(res.pagination?.total || 0);
      } else {
        setError(res?.message || "Failed to load");
      }
    } catch (e) {
      setError(e?.message || "Failed to load redemptions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRedemptions(page);
  }, [page, fetchRedemptions]);

  const handleDelete = useCallback(
    async (id) => {
      const confirmed = await confirmDanger(
        "Delete this referral redemption record? This action cannot be undone.",
        "Delete referral record"
      );
      if (!confirmed) return;
      setDeletingId(id);
      try {
        const res = await adminService.deleteReferralRedemption(id);
        if (res?.success) {
          toast.success("Referral record deleted");
          await fetchRedemptions(page);
        } else {
          toast.error(res?.message || "Failed to delete referral record");
        }
      } catch (e) {
        toast.error(e?.message || "Failed to delete referral record");
      } finally {
        setDeletingId(null);
      }
    },
    [fetchRedemptions, page]
  );

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Referral program</h2>
        <p className="text-sm text-gray-600 mb-6">
          Rewards are fixed in the app: <strong>2</strong> referrals → 1h boost;{" "}
          <strong>5</strong> → 30 connections/day for 7 days; <strong>10</strong> → 1 day premium.
          Each row is one successful referral (referee + device). Referrer totals are shown under{" "}
          <strong>Users</strong> and <strong>User Profiles</strong>.
        </p>

        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
            {error}
          </div>
        )}

        {loading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : redemptions.length === 0 ? (
          <p className="text-gray-500 py-8 text-center">No referrals found.</p> 
        ): (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead>
                <tr className="border-b">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Referrer</th>
                  <th className="py-2 pr-4">Referee</th>
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Device</th>
                  <th className="py-2">When</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4">{r.id}</td>
                    <td className="py-2 pr-4">
                      {r.referrerDisplayName ||
                        `${r.referrer_firstName || ""} ${r.referrer_lastName || ""}`.trim() ||
                        "-"}
                      <div className="text-xs text-gray-500">
                        {r.referrer_email || r.referrer_email_snapshot || "-"}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      {r.refereeDisplayName ||
                        `${r.referee_firstName || ""} ${r.referee_lastName || ""}`.trim() ||
                        "-"}
                      <div className="text-xs text-gray-500">
                        {r.referee_email || r.referee_email_snapshot || "-"}
                      </div>
                    </td>
                    <td className="py-2 pr-4 font-mono">{r.referral_code}</td>
                    <td className="py-2 pr-4 font-mono text-xs max-w-[140px] truncate" title={r.device_id}>
                      {r.device_id}
                    </td>
                    <td className="py-2 text-xs text-gray-600">{r.created_at}</td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(r.id)}
                        disabled={deletingId === r.id}
                        className="px-3 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {deletingId === r.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PaginationBar
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={20}
          label="redemptions"
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
